import { SelectProps, TextField } from '@material-ui/core';
import { StyledComponentProps } from '@material-ui/core/styles';
import { TextFieldProps } from '@material-ui/core/TextField';
import cx from 'classnames';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { ChevronDownIcon } from 'src/components/icons';
import { GraySmall } from 'src/theme/colors';

export type TextFieldSizeVariant = 'skinny' | 'medium' | 'tall';

const useStyles = makeStyles()((theme) => ({
  placeholder: {
    '& .MuiInputBase-input': {
      color: GraySmall,
    },
  },
  skinny: {
    '& .MuiInputBase-input': {
      '&:not(textarea)': {
        height: '30px',
      },
    },
  },
  medium: {
    '& .MuiInputBase-input': {
      '&:not(textarea)': {
        height: '36px',
      },
    },
  },
  tall: {
    '& .MuiInputBase-input': {
      '&:not(textarea)': {
        height: '48px',
      },
    },
  },
  dropdownIcon: {
    height: '15px',
    width: '15px',
    color: GraySmall,
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(0.75),
  },
}));

export type BaseTextFieldProps = TextFieldProps & {
  sizeVariant?: TextFieldSizeVariant;
  isPlaceholder?: boolean;
  additionalSelectProps?: Partial<SelectProps>;
  classes?: StyledComponentProps['classes'];
};

export const BaseTextField: React.FC<BaseTextFieldProps> = ({
  sizeVariant = 'skinny',
  additionalSelectProps,
  onBlur,
  isPlaceholder,
  onChange,
  className,
  helperText,
  ...textFieldProps
}) => {
  const { classes } = useStyles();
  const classNameOptions = React.useMemo(() => {
    const options = {
      [classes.placeholder]: isPlaceholder,
    };
    if (className) {
      options[className] = true;
    }
    return options;
  }, [className, isPlaceholder]);
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const stagedEvent = event;
    // For cases where value may not be simply string, we do not want to call onChange event
    if (
      event.target.value &&
      typeof event.target.value === 'string' &&
      !textFieldProps.select && // No trimming required for dropdowns
      !textFieldProps?.inputProps?.isautocomplete // No trimming for auto-complete controls
    ) {
      stagedEvent.target.value = event.target.value.trim();
      onChange?.(stagedEvent);
    }
    // onBlur to be called regardless
    onBlur?.(stagedEvent);
  };

  return (
    <TextField
      SelectProps={{
        classes: { icon: classes.dropdownIcon },
        IconComponent: ChevronDownIcon,
        ...additionalSelectProps,
      }}
      onBlur={handleBlur}
      onChange={onChange}
      classes={{
        root: classes[sizeVariant],
      }}
      className={cx(classNameOptions)}
      InputProps={{
        ...textFieldProps.InputProps,
        classes: {
          ...textFieldProps.InputProps?.classes,
          root: '[&.Mui-error_fieldset]:!border-error-primary',
        },
      }}
      {...textFieldProps}
      helperText={
        textFieldProps.error &&
        typeof helperText === 'string' &&
        helperText.trim().length > 0 ? (
          <ErrorMessage message={helperText} />
        ) : (
          helperText
        )
      }
    />
  );
};
